import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { COULEURS_DU_DESIGN, estHex, nomDeCouleur } from '@taki/data-model';
import type { Couleur } from '@taki/data-model';
import { cn } from '../lib/cn.js';
import { couleurCss } from '../lib/couleurs.js';

/**
 * LE SELECTEUR DE COULEUR D'UNE OPTION (brique du Gabarit Parametres,
 * `docs/socle/cartes-kanban.md`, section 3).
 *
 * Le DECLENCHEUR est la pastille de la colonne « Couleur » des reglages : une
 * boite de 7 x 8 bordee, un point de couleur au centre. Au clic, un petit
 * encadre : la grille des vingt couleurs du design (5 x 4), puis une ligne HEX
 * pour une couleur precise, gardee telle quelle.
 *
 * - Une pastille cliquee ecrit sa clef et referme.
 * - Le HEX s'ecrit sur Entree (et referme) ou a la perte du focus, s'il est
 *   valide (`#rgb` / `#rrggbb`, le `#` s'ajoute s'il manque). Invalide : le
 *   champ se marque en erreur, rien n'est ecrit.
 * - Echap et le clic dehors referment ; le focus revient au declencheur.
 *
 * DEUX MODES. Par defaut le selecteur porte son declencheur (la pastille ci-dessus).
 * PILOTE (`ancre` fournie), il n'en dessine aucun : l'appelant tient l'ouverture
 * (`ouvert`, `onFermer`) et le selecteur se pose sous l'element qu'il donne, la
 * pastille d'une fiche par exemple. Dans ce mode il ne rend JAMAIS le focus de
 * lui-meme : `onFermer` dit s'il faut le rendre, et c'est l'appelant qui sait ou.
 * `onRetirer` ajoute en tete de l'encadre un choix « Aucune couleur » (la
 * couleur d'une FICHE est facultative, celle d'une option ne l'est pas).
 *
 * L'encadre est PORTE hors du flux (le meme mecanisme que `StatusEditor`) : la
 * table des reglages defile en largeur et rognerait un panneau absolu. Dans une
 * fenetre (Modal), il est porte DANS la fenetre, qui rend inerte tout le reste
 * de la page ; `data-garde-echap` lui garde sa touche Echap.
 */
export interface SelecteurCouleurProps {
  /** La couleur courante. `null` ou absente : aucune (la fiche sans couleur). */
  value: Couleur | null | undefined;
  onChange(c: Couleur): void;
  /** aria-label du declencheur, ex. « Couleur de En cours ». Celui de l'encadre en mode pilote. */
  label: string;
  disabled?: boolean;
  className?: string;
  /**
   * MODE PILOTE : l'element sous lequel l'encadre se pose. Present, aucun
   * declencheur n'est dessine et l'ouverture suit `ouvert`.
   */
  ancre?: HTMLElement | null;
  /** Mode pilote : l'encadre est-il ouvert ? */
  ouvert?: boolean;
  /** Mode pilote : l'encadre se ferme (choix, Echap, clic dehors). `rendreLeFocus` : l'appelant le rend a son ancre. */
  onFermer?(rendreLeFocus: boolean): void;
  /** Ajoute le choix « Aucune couleur », qui appelle ceci. Absent : pas de choix. */
  onRetirer?(): void;
  /**
   * Mode pilote : les boutons de l'appelant qui OUVRENT et FERMENT l'encadre.
   * Un clic sur eux n'est pas un clic dehors : sans cette exception, le
   * `pointerdown` fermait l'encadre et le clic qui suit le rouvrait aussitot.
   * C'est l'appelant qui bascule `ouvert`.
   */
  declencheurs?: ReadonlyArray<{ readonly current: HTMLElement | null }>;
}

/** Ce qui sait dire s'il contient un noeud : un element du DOM, ou un double dans un test. */
interface Contenant { contains(noeud: Node): boolean }

/**
 * UN POINTERDOWN EST-IL UN CLIC DEHORS ? Pure, pour qu'un test la tienne sans
 * DOM. Ni le panneau, ni le declencheur dessine, ni un des `declencheurs` de
 * l'appelant : un clic sur l'icone qui a ouvert l'encadre le ferme par
 * l'appelant, il ne le ferme pas deux fois pour le rouvrir.
 */
export function estUnClicDehors(
  cible: Node | null,
  dedans: ReadonlyArray<Contenant | null | undefined>,
): boolean {
  if (cible == null) return true;
  return !dedans.some((element) => element?.contains(cible) === true);
}

const COLONNES = 5;
const MARGE = 8;
const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';

/** La saisie, ramenee a un hex : espaces retires, `#` ajoute s'il manque. Rend null si ce n'est pas un hex. */
function hexDeSaisie(saisie: string): `#${string}` | null {
  const brut = saisie.trim();
  if (brut === '') return null;
  const hex = brut.startsWith('#') ? brut : `#${brut}`;
  return estHex(hex) ? hex : null;
}

export function SelecteurCouleur({
  value,
  onChange,
  label,
  disabled = false,
  className,
  ancre: ancreExterne,
  ouvert: ouvertPilote,
  onFermer,
  onRetirer,
  declencheurs,
}: SelecteurCouleurProps): JSX.Element {
  const declencheur = useRef<HTMLButtonElement>(null);
  const panneau = useRef<HTMLDivElement>(null);
  const pastilles = useRef<(HTMLButtonElement | null)[]>([]);
  const [ancre, setAncre] = useState<DOMRect | null>(null);
  const [position, setPosition] = useState<CSSProperties>({ visibility: 'hidden' });
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState(false);
  const idHex = useId();
  /* Lus par les ecouteurs du document, poses une fois a l'ouverture. */
  const saisieRef = useRef('');
  const valeurRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onFermerRef = useRef(onFermer);
  const ancreExterneRef = useRef(ancreExterne);
  const declencheursRef = useRef(declencheurs);
  declencheursRef.current = declencheurs;
  saisieRef.current = saisie;
  valeurRef.current = value;
  onChangeRef.current = onChange;
  onFermerRef.current = onFermer;
  ancreExterneRef.current = ancreExterne;
  const pilote = ancreExterne !== undefined;
  const ouvert = ancre != null;
  const rang = COULEURS_DU_DESIGN.findIndex((couleur) => couleur.clef === value);
  const apercu = hexDeSaisie(saisie);
  /* Ce sous quoi l'encadre se pose et ou le focus revient : l'ancre donnee, sinon le declencheur. */
  const pointDAncrage = (): HTMLElement | null => ancreExterneRef.current ?? declencheur.current;

  const fermer = (rendreLeFocus: boolean): void => {
    /* Le blur du champ qui suit la fermeture n'ecrit plus rien : deja ecrit, ou abandonne (Echap). */
    saisieRef.current = '';
    setAncre(null);
    setErreur(false);
    if (ancreExterneRef.current !== undefined) onFermerRef.current?.(rendreLeFocus);
    else if (rendreLeFocus) declencheur.current?.focus();
  };
  const ouvrir = (): void => {
    const point = pointDAncrage();
    if (disabled || point == null) return;
    setSaisie(value != null && estHex(value) ? value : '');
    setErreur(false);
    setPosition({ visibility: 'hidden' });
    setAncre(point.getBoundingClientRect());
  };

  /* MODE PILOTE : l'ouverture suit la prop. Ouvrir quand elle devient vraie, et
     refermer SANS rappeler `onFermer` quand elle redevient fausse (l'appelant le
     sait deja : c'est lui qui l'a dit). */
  useEffect(() => {
    if (!pilote) return;
    if (ouvertPilote === true && ancre == null) ouvrir();
    if (ouvertPilote !== true && ancre != null) {
      /* Fermer par le declencheur vaut un clic dehors : un hex tape s'ecrit. */
      if (hexDeSaisie(saisieRef.current) !== null) ecrireLaSaisie();
      saisieRef.current = '';
      setAncre(null);
      setErreur(false);
    }
    /* `ouvrir` ne lit que des refs et des valeurs stables a l'instant ou la prop change. */
  }, [pilote, ouvertPilote]);
  const choisir = (couleur: Couleur): void => {
    if (couleur !== value) onChange(couleur);
    fermer(true);
  };
  /* Rend vrai si la saisie est ecrite (ou vide), faux si elle est refusee. */
  const ecrireLaSaisie = (): boolean => {
    if (saisieRef.current.trim() === '') return true;
    const hex = hexDeSaisie(saisieRef.current);
    if (hex === null) {
      setErreur(true);
      return false;
    }
    setErreur(false);
    if (hex !== valeurRef.current) onChangeRef.current(hex);
    return true;
  };

  /* Placer l'encadre sous le declencheur, aligne a sa gauche, retourne au-dessus
     s'il deborde en bas, ramene dans la fenetre s'il deborde a droite. */
  useLayoutEffect(() => {
    if (ancre == null || panneau.current == null) return;
    const boite = panneau.current.getBoundingClientRect();
    const sous = ancre.bottom + 4;
    const top = sous + boite.height > window.innerHeight - MARGE && ancre.top - 4 - boite.height > MARGE
      ? ancre.top - 4 - boite.height : sous;
    const left = Math.max(MARGE, Math.min(ancre.left, window.innerWidth - boite.width - MARGE));
    /* Pose tout de suite, avant le focus : un element `visibility: hidden` ne le prend pas. */
    Object.assign(panneau.current.style, { top: `${top}px`, left: `${left}px`, visibility: 'visible' });
    setPosition({ top, left });
    (pastilles.current[rang >= 0 ? rang : 0] ?? panneau.current).focus({ preventScroll: true });
    /* Ne depend que de l'ouverture : le rang n'est lu qu'a ce moment-la. */
  }, [ancre]);

  /* Clic dehors, Echap hors du panneau, defilement et redimensionnement ferment. */
  useEffect(() => {
    if (!ouvert) return;
    const dehors = (event: PointerEvent): void => {
      const cible = event.target as Node | null;
      if (!estUnClicDehors(cible, [panneau.current, declencheur.current, ...(declencheursRef.current ?? []).map((bouton) => bouton.current)])) return;
      /* La perte du focus vaut ecriture : le panneau se demonte avant le blur du champ. */
      if (hexDeSaisie(saisieRef.current) !== null) ecrireLaSaisie();
      fermer(false);
      /* Le focus revient au declencheur, sauf si le clic l'a donne a autre chose. */
      requestAnimationFrame(() => {
        if (document.activeElement == null || document.activeElement === document.body) declencheur.current?.focus();
      });
    };
    const echap = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      fermer(true);
    };
    const defile = (event: Event): void => {
      if (event.target instanceof Node && panneau.current?.contains(event.target)) return;
      fermer(false);
    };
    const redimensionne = (): void => fermer(false);
    document.addEventListener('pointerdown', dehors, true);
    document.addEventListener('keydown', echap);
    window.addEventListener('scroll', defile, true);
    window.addEventListener('resize', redimensionne);
    return () => {
      document.removeEventListener('pointerdown', dehors, true);
      document.removeEventListener('keydown', echap);
      window.removeEventListener('scroll', defile, true);
      window.removeEventListener('resize', redimensionne);
    };
    /* `fermer` et `ecrireLaSaisie` ne lisent que des refs et des setters stables. */
  }, [ouvert]);

  /* Les fleches parcourent la grille (5 colonnes), sans en sortir. */
  const auClavierDeLaGrille = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const pas = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: COLONNES, ArrowUp: -COLONNES }[event.key];
    if (pas === undefined) return;
    event.preventDefault();
    const courant = pastilles.current.findIndex((pastille) => pastille === document.activeElement);
    const suivant = Math.max(0, Math.min(COULEURS_DU_DESIGN.length - 1, (courant < 0 ? 0 : courant) + pas));
    pastilles.current[suivant]?.focus();
  };

  /* Lu seulement ouvert : rendu cote serveur (un test), `document` n'existe pas. */
  const cibleDuPortail = ouvert ? (pointDAncrage()?.closest<HTMLElement>('[role="dialog"]') ?? document.body) : null;

  return (
    <>
      {!pilote && (
        <button
          ref={declencheur}
          type="button"
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={ouvert}
          title={value == null ? 'Aucune couleur' : `Couleur : ${nomDeCouleur(value)}`}
          disabled={disabled}
          onClick={() => (ouvert ? fermer(true) : ouvrir())}
          className={cn(
            'inline-flex h-7 w-8 cursor-pointer items-center justify-center rounded-sm border border-border bg-surface',
            'disabled:cursor-not-allowed',
            FOCUS,
            className,
          )}
        >
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: couleurCss(value ?? undefined) }} />
        </button>
      )}
      {ouvert && cibleDuPortail !== null && createPortal(
        <div
          ref={panneau}
          role="dialog"
          aria-label={label}
          tabIndex={-1}
          data-garde-echap=""
          style={{ position: 'fixed', ...position }}
          className="z-[100] flex w-max flex-col gap-2 rounded-md border border-border bg-surface p-2 shadow-pop outline-none"
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            fermer(true);
          }}
          onBlur={(event) => {
            /* Le focus part ailleurs au clavier (Tab) : l'encadre se referme. Un
               clic dehors est deja traite par `pointerdown`. */
            const vers = event.relatedTarget;
            /* Vers un declencheur de l'appelant (l'icone qui l'a ouvert) : son clic
               bascule `ouvert`, fermer ici le ferait rouvrir par ce clic. */
            if (vers == null || !estUnClicDehors(vers, [panneau.current, declencheur.current, ...(declencheursRef.current ?? []).map((bouton) => bouton.current)])) return;
            fermer(false);
          }}
        >
          <div role="radiogroup" aria-label="Couleurs du design" className="grid grid-cols-5 gap-1" onKeyDown={auClavierDeLaGrille}>
            {COULEURS_DU_DESIGN.map((couleur, index) => {
              const choisie = couleur.clef === value;
              return (
                <button
                  key={couleur.clef}
                  ref={(noeud) => {
                    pastilles.current[index] = noeud;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={choisie}
                  aria-label={couleur.nom}
                  title={couleur.nom}
                  tabIndex={index === (rang >= 0 ? rang : 0) ? 0 : -1}
                  onClick={() => choisir(couleur.clef)}
                  className={cn(
                    'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border transition-colors',
                    choisie ? 'border-ink-700' : 'border-transparent hover:border-border-strong',
                    FOCUS,
                  )}
                >
                  <span className="h-4 w-4 rounded-full" style={{ backgroundColor: couleurCss(couleur.clef) }} />
                </button>
              );
            })}
          </div>
          {onRetirer !== undefined && (
            <button
              type="button"
              onClick={() => {
                onRetirer();
                fermer(true);
              }}
              className={cn(
                'flex h-7 w-full cursor-pointer items-center gap-2 rounded-sm px-1.5 text-left text-[0.8125rem] text-ink-700 transition-colors hover:bg-surface-3 hover:text-ink-900',
                FOCUS,
              )}
            >
              <span aria-hidden className="h-4 w-4 shrink-0 rounded-full border border-dashed border-border-strong" />
              Aucune couleur
            </button>
          )}
          <div className="flex items-center gap-2 border-t border-border-soft pt-2">
            <label htmlFor={idHex} className="text-[0.6875rem] font-medium text-ink-500">
              HEX
            </label>
            <input
              id={idHex}
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              maxLength={7}
              placeholder="#RRGGBB"
              aria-invalid={erreur}
              value={saisie}
              onChange={(event) => {
                setSaisie(event.target.value);
                setErreur(false);
              }}
              onBlur={() => {
                ecrireLaSaisie();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                if (saisie.trim() !== '' && ecrireLaSaisie()) fermer(true);
              }}
              className={cn(
                'h-7 w-24 min-w-0 rounded-sm border bg-surface px-2 font-mono text-xs text-ink-900 placeholder:text-ink-300',
                erreur ? 'border-danger' : 'border-border',
                FOCUS,
              )}
            />
            <span
              aria-hidden
              className={cn('h-4 w-4 shrink-0 rounded-full border', apercu === null ? 'border-border' : 'border-transparent')}
              style={apercu === null ? undefined : { backgroundColor: apercu }}
            />
          </div>
          {erreur ? (
            <p role="alert" className="text-[0.6875rem] text-danger">
              Couleur invalide
            </p>
          ) : null}
        </div>,
        cibleDuPortail,
      )}
    </>
  );
}
